import { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Phone, 
  MapPin, 
  Calendar, 
  ExternalLink, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Loader2,
  Filter,
  Paintbrush,
  Euro,
  Building,
  User,
  Mail,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

// Status configurations
const STATUS_CONFIG = {
  neu: { label: 'Neu', color: 'bg-blue-500', icon: Clock },
  kontaktiert: { label: 'Kontaktiert', color: 'bg-yellow-500', icon: Phone },
  interessiert: { label: 'Interessiert', color: 'bg-purple-500', icon: MessageSquare },
  gewonnen: { label: 'Gewonnen', color: 'bg-green-500', icon: CheckCircle },
  verloren: { label: 'Verloren', color: 'bg-gray-500', icon: XCircle },
};

// Regions
const REGIONS = ['Alle', 'Köln', 'Dormagen', 'Neuss', 'Düsseldorf'];

// Job types
const JOB_TYPES = {
  innen: 'Innenarbeiten',
  aussen: 'Außenarbeiten',
  tapezieren: 'Tapezieren',
  lackieren: 'Lackieren',
  fassade: 'Fassade',
  renovierung: 'Renovierung',
  sonstiges: 'Sonstiges'
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState('alle');
  const [filterRegion, setFilterRegion] = useState('Alle');
  const [searchStats, setSearchStats] = useState(null);
  const [lastSearch, setLastSearch] = useState(null);

  // Load leads
  const loadLeads = useCallback(async () => {
    try {
      const response = await api.get('/admin/leads');
      setLeads(response.data.leads || []);
      setSearchStats(response.data.stats || null);
      setLastSearch(response.data.last_search || null);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Fehler beim Laden der Aufträge');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Trigger manual search
  const triggerSearch = async () => {
    setSearching(true);
    try {
      const response = await api.post('/admin/leads/search');
      toast.success(`${response.data.new_leads} neue Aufträge gefunden!`);
      loadLeads();
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Fehler bei der Suche');
    } finally {
      setSearching(false);
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status: newStatus });
      setLeads(prev => prev.map(l => 
        l.id === leadId ? { ...l, status: newStatus } : l
      ));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Add note to lead
  const addNote = async (leadId, note) => {
    try {
      await api.post(`/admin/leads/${leadId}/notes`, { note });
      loadLeads();
      toast.success('Notiz hinzugefügt');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    if (filterStatus !== 'alle' && lead.status !== filterStatus) return false;
    if (filterRegion !== 'Alle' && !lead.location?.toLowerCase().includes(filterRegion.toLowerCase())) return false;
    return true;
  });

  // Stats
  const stats = {
    total: leads.length,
    neu: leads.filter(l => l.status === 'neu').length,
    kontaktiert: leads.filter(l => l.status === 'kontaktiert').length,
    gewonnen: leads.filter(l => l.status === 'gewonnen').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#9B1B30] to-[#c42847] rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Paintbrush className="w-8 h-8" />
              Auftrags-Finder
            </h1>
            <p className="text-white/80 mt-1">
              Automatische Suche nach Maleraufträgen in Köln, Dormagen, Neuss & Düsseldorf
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={triggerSearch}
              disabled={searching}
              className="bg-white text-[#9B1B30] hover:bg-white/90"
            >
              {searching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {searching ? 'Suche läuft...' : 'Jetzt suchen'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold">{stats.total}</div>
            <div className="text-white/70 text-sm">Gesamt</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-300">{stats.neu}</div>
            <div className="text-white/70 text-sm">Neue Leads</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold text-yellow-300">{stats.kontaktiert}</div>
            <div className="text-white/70 text-sm">Kontaktiert</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4">
            <div className="text-3xl font-bold text-green-300">{stats.gewonnen}</div>
            <div className="text-white/70 text-sm">Gewonnen</div>
          </div>
        </div>

        {lastSearch && (
          <div className="mt-4 text-white/60 text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Letzte Suche: {new Date(lastSearch).toLocaleString('de-DE')}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterRegion} onValueChange={setFilterRegion}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent>
              {REGIONS.map(region => (
                <SelectItem key={region} value={region}>{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-gray-500">
            {filteredLeads.length} Ergebnisse
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leads List */}
        <div className="lg:col-span-2 space-y-3">
          {filteredLeads.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
              <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Keine Aufträge gefunden</p>
              <p className="text-gray-400 text-sm mt-1">Klicken Sie auf "Jetzt suchen" um neue Aufträge zu finden</p>
            </div>
          ) : (
            filteredLeads.map(lead => (
              <div
                key={lead.id}
                onClick={() => setSelectedLead(lead)}
                className={`bg-white rounded-xl p-4 border cursor-pointer transition-all hover:shadow-md ${
                  selectedLead?.id === lead.id 
                    ? 'border-[#9B1B30] shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${STATUS_CONFIG[lead.status]?.color} text-white`}>
                        {STATUS_CONFIG[lead.status]?.label}
                      </Badge>
                      {lead.job_type && (
                        <Badge variant="outline">{JOB_TYPES[lead.job_type] || lead.job_type}</Badge>
                      )}
                      {lead.is_new && (
                        <Badge className="bg-blue-500 text-white">NEU</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 truncate">{lead.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{lead.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {lead.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(lead.found_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  </div>
                  {lead.estimated_value && (
                    <div className="text-right">
                      <div className="text-lg font-bold text-[#9B1B30]">
                        ~{lead.estimated_value}€
                      </div>
                      <div className="text-xs text-gray-400">geschätzt</div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Lead Details */}
        <div className="lg:col-span-1">
          {selectedLead ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-4">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Details</h3>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <Label className="text-gray-500 text-xs">Titel</Label>
                  <p className="font-medium text-gray-900">{selectedLead.title}</p>
                </div>

                <div>
                  <Label className="text-gray-500 text-xs">Beschreibung</Label>
                  <p className="text-gray-700 text-sm">{selectedLead.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs">Ort</Label>
                    <p className="text-gray-900 flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-[#9B1B30]" />
                      {selectedLead.location}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Gefunden am</Label>
                    <p className="text-gray-900">
                      {new Date(selectedLead.found_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>

                {selectedLead.contact_name && (
                  <div>
                    <Label className="text-gray-500 text-xs">Kontakt</Label>
                    <p className="text-gray-900 flex items-center gap-1">
                      <User className="w-4 h-4 text-[#9B1B30]" />
                      {selectedLead.contact_name}
                    </p>
                  </div>
                )}

                {selectedLead.contact_phone && (
                  <div>
                    <Label className="text-gray-500 text-xs">Telefon</Label>
                    <a 
                      href={`tel:${selectedLead.contact_phone}`}
                      className="text-[#9B1B30] font-medium flex items-center gap-1 hover:underline"
                    >
                      <Phone className="w-4 h-4" />
                      {selectedLead.contact_phone}
                    </a>
                  </div>
                )}

                {selectedLead.contact_email && (
                  <div>
                    <Label className="text-gray-500 text-xs">Email</Label>
                    <a 
                      href={`mailto:${selectedLead.contact_email}`}
                      className="text-[#9B1B30] flex items-center gap-1 hover:underline"
                    >
                      <Mail className="w-4 h-4" />
                      {selectedLead.contact_email}
                    </a>
                  </div>
                )}

                {selectedLead.source_url && (
                  <div>
                    <Label className="text-gray-500 text-xs">Quelle</Label>
                    <a 
                      href={selectedLead.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#9B1B30] flex items-center gap-1 hover:underline text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Original ansehen
                    </a>
                  </div>
                )}

                {/* Status Change */}
                <div className="pt-4 border-t border-gray-100">
                  <Label className="text-gray-500 text-xs mb-2 block">Status ändern</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <Button
                          key={key}
                          size="sm"
                          variant={selectedLead.status === key ? 'default' : 'outline'}
                          className={selectedLead.status === key ? config.color : ''}
                          onClick={() => updateLeadStatus(selectedLead.id, key)}
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-4 border-t border-gray-100 space-y-2">
                  {selectedLead.contact_phone && (
                    <Button 
                      className="w-full bg-green-500 hover:bg-green-600"
                      onClick={() => window.open(`tel:${selectedLead.contact_phone}`)}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Jetzt anrufen
                    </Button>
                  )}
                  {selectedLead.source_url && (
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(selectedLead.source_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Anzeige öffnen
                    </Button>
                  )}
                </div>

                {/* Notes */}
                {selectedLead.notes && selectedLead.notes.length > 0 && (
                  <div className="pt-4 border-t border-gray-100">
                    <Label className="text-gray-500 text-xs mb-2 block">Notizen</Label>
                    <div className="space-y-2">
                      {selectedLead.notes.map((note, idx) => (
                        <div key={idx} className="bg-gray-50 rounded p-2 text-sm">
                          <p className="text-gray-700">{note.text}</p>
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(note.created_at).toLocaleString('de-DE')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Paintbrush className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Wählen Sie einen Auftrag aus</p>
              <p className="text-gray-400 text-sm mt-1">um Details zu sehen</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
